import React, { useEffect, useRef, useState } from 'react';
import { isTextSelectionClick, rememberSelectionOnPointerDown } from './advisoryTextSelection';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputBase,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { useUntypedTranslation } from '../../../hooks/useUntypedTranslation';
import {
  validateReportingPeriodV01,
  type AdvisoryReportingQuarter,
  type AdvisoryReportingYearType,
  type ReportingPeriodV01,
} from '../../../hooks/advisory/advisoryReportingPeriod';
import { normalizeControlCharacters } from '../../../redux/slices/advisory/advisoryTextSanitize';
import { eyebrow, focusRing, meta, mono } from '../../../styles/visualLanguage';

interface RangeOption {
  value: string;
  quarter: AdvisoryReportingQuarter | null;
  endQuarter: AdvisoryReportingQuarter | null;
  labelKey: string;
}

export const ADVISORY_PERIOD_RANGE_OPTIONS: readonly RangeOption[] = [
  { value: 'year', quarter: null, endQuarter: null, labelKey: 'advisory:period.rangeYear' },
  { value: 'q1', quarter: 1, endQuarter: null, labelKey: 'advisory:period.rangeQ1' },
  { value: 'q1-q2', quarter: 1, endQuarter: 2, labelKey: 'advisory:period.rangeQ1Q2' },
  { value: 'q1-q3', quarter: 1, endQuarter: 3, labelKey: 'advisory:period.rangeQ1Q3' },
  { value: 'q1-q4', quarter: 1, endQuarter: 4, labelKey: 'advisory:period.rangeQ1Q4' },
  { value: 'q2', quarter: 2, endQuarter: null, labelKey: 'advisory:period.rangeQ2' },
  { value: 'q2-q3', quarter: 2, endQuarter: 3, labelKey: 'advisory:period.rangeQ2Q3' },
  { value: 'q2-q4', quarter: 2, endQuarter: 4, labelKey: 'advisory:period.rangeQ2Q4' },
  { value: 'q3', quarter: 3, endQuarter: null, labelKey: 'advisory:period.rangeQ3' },
  { value: 'q3-q4', quarter: 3, endQuarter: 4, labelKey: 'advisory:period.rangeQ3Q4' },
  { value: 'q4', quarter: 4, endQuarter: null, labelKey: 'advisory:period.rangeQ4' },
] as const;

const rangeValue = (period: ReportingPeriodV01): string =>
  ADVISORY_PERIOD_RANGE_OPTIONS.find(
    option => option.quarter === period.quarter && option.endQuarter === period.endQuarter
  )?.value ?? 'year';

type PeriodField = 'basis' | 'year' | 'range';

const inlineEditSx = (theme: Theme) => ({
  minWidth: 0,
  padding: 0,
  borderRadius: 0,
  fontSize: 'inherit',
  fontFamily: 'inherit',
  fontWeight: 'inherit',
  lineHeight: 'inherit',
  verticalAlign: 'baseline',
  textTransform: 'none',
  userSelect: 'text',
  color: theme.colors.lavenderDeep,
  textDecorationLine: 'underline',
  textDecorationStyle: 'dotted',
  textDecorationThickness: '2px',
  textUnderlineOffset: '0.2em',
  '&:hover': {
    backgroundColor: 'transparent',
    textDecoration: 'underline dotted',
  },
});

const parseYear = (value: string): number | null => {
  const normalized = normalizeControlCharacters(value);
  const year = Number(normalized);
  return /^\d{4}$/.test(normalized) && year >= 1900 && year <= 9999 ? year : null;
};

export interface AdvisoryPeriodEditorProps {
  value: ReportingPeriodV01 | null;
  onChange: (period: ReportingPeriodV01) => void;
  onEditStart?: () => void;
  onEditCancel?: () => void;
  /** Keep the staged period and its opening values across live snapshot and busy changes. */
  preserveStagedEditsOnBusy?: boolean;
  busy?: boolean;
  inline?: boolean;
  autoFocusYear?: boolean;
  problem?: boolean;
}

/** Controlled editor: selecting a value changes the draft, never confirms it. */
export function AdvisoryPeriodEditor({
  value: storedValue,
  onChange,
  onEditStart,
  onEditCancel,
  preserveStagedEditsOnBusy = false,
  busy = false,
  inline = false,
  autoFocusYear = false,
  problem = false,
}: AdvisoryPeriodEditorProps) {
  const { t } = useUntypedTranslation();
  const [initialYear] = useState(() => new Date().getFullYear());
  // Center the empty picker without writing a proposal until the user selects a year.
  const value: ReportingPeriodV01 = storedValue ?? {
    year: initialYear,
    quarter: null,
    endQuarter: null,
    yearType: 'calendar',
    fiscalStartMonth: 1,
  };
  const missing = storedValue === null;
  const year = String(value.year);
  const yearType = value.yearType;
  const range = rangeValue(value);
  const basisButtonRef = useRef<HTMLButtonElement>(null);
  const yearButtonRef = useRef<HTMLButtonElement>(null);
  const menuTriggerRef = useRef<HTMLElement | null>(null);
  const [menuField, setMenuField] = useState<'year' | 'range'>('year');
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [basisDialogOpen, setBasisDialogOpen] = useState(false);
  const [basisDraft, setBasisDraft] = useState<AdvisoryReportingYearType>(yearType);
  const [monthDraft, setMonthDraft] = useState(value.fiscalStartMonth);
  const openingValue = useRef<ReportingPeriodV01 | null>(null);
  useEffect(() => {
    if (busy && !preserveStagedEditsOnBusy) {
      setMenuAnchor(null);
      setBasisDialogOpen(false);
    }
  }, [busy, preserveStagedEditsOnBusy]);
  const [yearDialogOpen, setYearDialogOpen] = useState(false);
  const [yearDraft, setYearDraft] = useState(year);
  const draftYear = parseYear(yearDraft);
  const selectedRange = ADVISORY_PERIOD_RANGE_OPTIONS.find(option => option.value === range);
  const rangeLabel = t(selectedRange?.labelKey ?? 'advisory:period.rangeYear');
  const basisLabel = t(`advisory:period.${yearType}`);
  const fieldLabels: Record<PeriodField, string> = {
    basis: t('advisory:period.basisLabel'),
    year: t('advisory:period.yearLabel'),
    range: t('advisory:period.rangeLabel'),
  };

  const openMenu = (field: PeriodField, anchor: HTMLElement) => {
    if (busy) return;
    openingValue.current = { ...value };
    onEditStart?.();
    if (field === 'basis') {
      setBasisDraft(value.yearType);
      setMonthDraft(value.yearType === 'fiscal' ? value.fiscalStartMonth : 2);
      setBasisDialogOpen(true);
      return;
    }
    menuTriggerRef.current = anchor;
    setMenuField(field);
    setMenuAnchor(anchor);
  };
  const cancelEdit = () => {
    openingValue.current = null;
    onEditCancel?.();
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    cancelEdit();
  };
  const editValue = () => (preserveStagedEditsOnBusy ? openingValue.current ?? value : value);
  const apply = (period: ReportingPeriodV01) => {
    onChange(period);
    openingValue.current = null;
  };
  const choose = (choice: string) => {
    if (busy) return;
    let next = editValue();
    if (menuField === 'year') {
      next = { ...next, year: Number(choice) };
    } else if (menuField === 'range') {
      const option = ADVISORY_PERIOD_RANGE_OPTIONS.find(option => option.value === choice);
      if (option) next = { ...next, quarter: option.quarter, endQuarter: option.endQuarter };
    }
    const validated = validateReportingPeriodV01(next);
    if (validated.ok) apply(validated.value);
    setMenuAnchor(null);
  };
  const startYearEdit = () => {
    if (busy) return;
    setYearDraft(String(editValue().year));
    setMenuAnchor(null);
    setYearDialogOpen(true);
  };
  const closeYearDialog = () => {
    setYearDialogOpen(false);
    setYearDraft(year);
    cancelEdit();
  };
  const closeBasisDialog = () => {
    setBasisDialogOpen(false);
    cancelEdit();
  };
  const applyBasis = () => {
    if (busy) return;
    const validated = validateReportingPeriodV01({
      ...editValue(),
      yearType: basisDraft,
      fiscalStartMonth: basisDraft === 'calendar' ? 1 : monthDraft,
    });
    if (validated.ok) apply(validated.value);
    setBasisDialogOpen(false);
  };
  const applyYear = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (draftYear === null || busy) return;
    apply({ ...editValue(), year: draftYear });
    setYearDialogOpen(false);
  };
  const yearStart = Math.max(1900, Number(year) - 5);
  const yearEnd = Math.min(9999, Number(year) + 5);
  const menuOptions =
    menuField === 'year'
      ? Array.from({ length: Math.max(0, yearEnd - yearStart + 1) }, (_, index) => {
          const value = String(yearStart + index);
          return { value, label: value, selected: !missing && value === year };
        })
      : ADVISORY_PERIOD_RANGE_OPTIONS.map(option => ({
          value: option.value,
          label: t(option.labelKey),
          selected: option.value === range,
        }));
  const fieldButton = (field: PeriodField, text: string, accessibleValue = text) => (
    <Button
      id={`advisory-period-${field}`}
      data-testid={missing ? 'advisory-review-add-period' : `advisory-period-${field}`}
      data-problem={problem || undefined}
      ref={field === 'year' ? yearButtonRef : field === 'basis' ? basisButtonRef : undefined}
      autoFocus={field === 'year' && autoFocusYear}
      variant="text"
      type="button"
      disableRipple
      disabled={busy}
      aria-label={
        missing
          ? t('advisory:review.missingPeriod')
          : t('advisory:period.editFieldLabel', {
              field: fieldLabels[field],
              value: accessibleValue,
            })
      }
      aria-haspopup={field === 'basis' ? 'dialog' : 'menu'}
      aria-expanded={
        field === 'basis' ? basisDialogOpen : menuAnchor !== null && menuField === field
      }
      aria-controls={
        field === 'basis' && basisDialogOpen
          ? 'advisory-period-basis-dialog'
          : menuAnchor !== null && menuField === field
          ? 'advisory-period-menu'
          : undefined
      }
      onPointerDown={rememberSelectionOnPointerDown}
      onClick={event => {
        if (!isTextSelectionClick(event)) openMenu(field, event.currentTarget);
      }}
      onKeyDown={event => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          openMenu(field, event.currentTarget);
        }
      }}
      sx={[
        inlineEditSx,
        focusRing,
        ...(problem ? [(theme: Theme) => ({ color: theme.colors.reviewWarning })] : []),
      ]}
    >
      {text}
    </Button>
  );

  return (
    <>
      <Box
        component={inline ? 'span' : 'div'}
        role="group"
        aria-label={inline ? t('advisory:period.summaryLabel') : undefined}
        aria-labelledby={inline ? undefined : 'advisory-period-editor-label'}
        data-testid="advisory-period-editor"
        sx={theme => ({
          display: inline ? 'inline' : 'block',
          marginTop: inline ? 0 : theme.visualPrimitives.goalReadOnlyGap,
        })}
      >
        {!inline ? (
          <Typography id="advisory-period-editor-label" sx={[eyebrow]}>
            {t('advisory:period.summaryLabel')}
          </Typography>
        ) : null}
        <Box
          component="span"
          sx={[
            mono,
            theme => ({
              display: 'inline-block',
              verticalAlign: 'baseline',
              // Allow period fields to wrap inside the statement's no-wrap punctuation group.
              whiteSpace: 'normal',
              marginTop: inline ? 0 : theme.visualPrimitives.rowGap,
              fontSize: inline ? 'inherit' : theme.visualPrimitives.goalReadOnlySize,
              lineHeight: theme.visualPrimitives.goalReadOnlyLineHeight,
            }),
          ]}
        >
          {missing ? (
            fieldButton('year', t('advisory:review.missingPeriod'))
          ) : (
            <>
              {fieldButton('basis', t(`advisory:period.${yearType}Short`), basisLabel)}
              <Box
                component="span"
                sx={{ display: 'inline-block', width: '0.35em', whiteSpace: 'pre' }}
              >
                {' '}
              </Box>
              {fieldButton('year', year)}
              <Box
                component="span"
                sx={{ display: 'inline-block', width: '0.35em', whiteSpace: 'pre' }}
              >
                {' '}
              </Box>
              {fieldButton('range', rangeLabel)}
            </>
          )}
        </Box>
        {!inline ? (
          <Typography sx={[meta, { marginTop: 1.5 }]}>{t('advisory:period.editHint')}</Typography>
        ) : null}
      </Box>
      <Menu
        id="advisory-period-menu"
        anchorEl={menuAnchor}
        open={menuAnchor !== null && (!busy || preserveStagedEditsOnBusy)}
        onClose={closeMenu}
        disableRestoreFocus
        MenuListProps={{ 'aria-label': fieldLabels[menuField] }}
        PaperProps={{ sx: theme => ({ backgroundColor: theme.palette.background.default }) }}
        TransitionProps={{
          onExited: () => {
            if (!yearDialogOpen && !basisDialogOpen) {
              const trigger = menuTriggerRef.current;
              (trigger?.isConnected ? trigger : yearButtonRef.current)?.focus();
            }
          },
        }}
      >
        {menuOptions.map(option => (
          <MenuItem
            key={option.value}
            role="menuitemradio"
            aria-checked={option.selected}
            selected={option.selected}
            autoFocus={missing && menuField === 'year' && option.value === year}
            disabled={busy}
            onClick={() => choose(option.value)}
          >
            {option.label}
          </MenuItem>
        ))}
        {menuField === 'year' ? (
          <MenuItem disabled={busy} onClick={startYearEdit}>
            {t('advisory:period.otherYearAction')}
          </MenuItem>
        ) : null}
      </Menu>
      <Dialog
        id="advisory-period-basis-dialog"
        open={basisDialogOpen}
        onClose={closeBasisDialog}
        aria-labelledby="advisory-period-basis-dialog-title"
        disableRestoreFocus
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: theme => ({ backgroundColor: theme.palette.background.default }) }}
        TransitionProps={{ onExited: () => basisButtonRef.current?.focus() }}
      >
        <DialogTitle id="advisory-period-basis-dialog-title">{fieldLabels.basis}</DialogTitle>
        <DialogContent>
          <RadioGroup
            aria-label={fieldLabels.basis}
            value={basisDraft}
            onChange={event => setBasisDraft(event.target.value as AdvisoryReportingYearType)}
          >
            {(['calendar', 'fiscal'] as const).map(basis => (
              <FormControlLabel
                key={basis}
                value={basis}
                label={t(`advisory:period.${basis}`)}
                disabled={busy}
                control={<Radio size="small" autoFocus={basisDraft === basis} />}
              />
            ))}
          </RadioGroup>
          {basisDraft === 'fiscal' && (
            <>
              <Typography id="advisory-period-month-label" sx={[meta, { mt: 2, mb: 1 }]}>
                {t('advisory:period.fiscalStartMonthLabel')}
              </Typography>
              <RadioGroup
                aria-labelledby="advisory-period-month-label"
                value={String(monthDraft)}
                onChange={event => setMonthDraft(Number(event.target.value))}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
                }}
              >
                {Array.from({ length: 12 }, (_, index) => (
                  <FormControlLabel
                    key={index + 1}
                    value={String(index + 1)}
                    label={t(`advisory:period.month${String(index + 1).padStart(2, '0')}`)}
                    disabled={busy}
                    control={<Radio size="small" sx={{ p: 0.75 }} />}
                    sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '14px' } }}
                  />
                ))}
              </RadioGroup>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            type="button"
            onClick={closeBasisDialog}
            disabled={busy && !preserveStagedEditsOnBusy}
            sx={[focusRing]}
          >
            {t('advisory:period.cancelYearAction')}
          </Button>
          <Button
            type="button"
            onClick={applyBasis}
            disabled={busy}
            data-testid="advisory-period-apply-basis"
            sx={[focusRing]}
          >
            {t('advisory:review.apply')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={yearDialogOpen}
        onClose={closeYearDialog}
        aria-labelledby="advisory-period-year-dialog-title"
        aria-describedby="advisory-period-year-help"
        disableRestoreFocus
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: theme => ({ backgroundColor: theme.palette.background.default }) }}
        TransitionProps={{ onExited: () => yearButtonRef.current?.focus() }}
      >
        <Box component="form" onSubmit={applyYear}>
          <DialogTitle id="advisory-period-year-dialog-title">
            {t('advisory:period.yearDialogTitle')}
          </DialogTitle>
          <DialogContent>
            <InputBase
              autoFocus
              fullWidth
              value={yearDraft}
              disabled={busy}
              onChange={event => setYearDraft(event.target.value)}
              inputProps={{
                inputMode: 'numeric',
                maxLength: 4,
                'aria-label': t('advisory:period.yearLabel'),
                'data-testid': 'advisory-period-year-input',
                'aria-invalid': draftYear === null,
                'aria-describedby': 'advisory-period-year-help',
              }}
              sx={theme => ({
                fontSize: theme.visualPrimitives.inputSize,
                lineHeight: theme.visualPrimitives.inputLineHeight,
                color: theme.colors.inkPrimary,
                borderBottom: `1px solid ${theme.visualPrimitives.border}`,
                '& .MuiInputBase-input': { padding: theme.visualPrimitives.inputPaddingY },
                '&:focus-within': { borderBottomColor: theme.colors.lavenderDeep },
              })}
            />
            <Typography id="advisory-period-year-help" sx={[meta, { marginTop: 1 }]}>
              {t('advisory:period.yearError')}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              type="button"
              onClick={closeYearDialog}
              disabled={busy && !preserveStagedEditsOnBusy}
              sx={[focusRing]}
            >
              {t('advisory:period.cancelYearAction')}
            </Button>
            <Button
              type="submit"
              disabled={draftYear === null || busy}
              data-testid="advisory-period-set-year"
              sx={[focusRing]}
            >
              {t('advisory:period.setYearAction')}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
}
